/**
 * <StudentSettingsPage> — « Paramètres » de l'élève (page).
 *
 * Les quatre onglets de l'écran d'aujourd'hui (Général, Adresse, Profil, Réglages) deviennent
 * quatre lignes : chacune ouvre sa page de formulaire, qui s'enregistre seule. Le design system
 * n'a pas d'onglets hauts, et un formulaire unique sous quatre onglets cachait à l'élève l'erreur
 * d'un onglet qu'il ne regardait pas.
 *
 * Une cinquième ligne, « Fuseau horaire », ouvre l'écran du design system commun à tous les comptes,
 * comme la ligne d'accès que l'ancien écran porte sous ses réglages.
 */
import React from "react"

import { SettingsSection, SettingsSectionSkeleton, type SettingsSectionRow } from "../../molecules/SettingsSection"
import { compact } from "../../utils/compact"
import { AccountSettingsTemplate } from "../../templates/AccountSettingsTemplate"

export type StudentSettingsPageProps = {
    onBack: () => void
    onOpenGeneral: () => void
    onOpenAddress: () => void
    onOpenProfile: () => void
    onOpenPreferences: () => void
    /** Ouvre l'écran « Fuseau horaire ». Absent = pas de ligne. */
    onOpenTimezone?: () => void
    /** La ville du fuseau du compte (« Oslo ») — en valeur de la ligne. */
    timezoneCity?: string | null
    /** Le compte n'est pas encore là. */
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    testID?: string
}

export function StudentSettingsPage(props: StudentSettingsPageProps) {
    const {
        onBack,
        onOpenGeneral,
        onOpenAddress,
        onOpenProfile,
        onOpenPreferences,
        onOpenTimezone,
        timezoneCity,
        loading = false,
        error,
        onRetry,
        testID,
    } = props

    return (
        <AccountSettingsTemplate
            testID={testID}
            title="Paramètres"
            onBack={onBack}
            loading={loading}
            skeleton={<SettingsSectionSkeleton rows={5} />}
            error={error}
            onRetry={onRetry}
        >
            <SettingsSection
                rows={compact<SettingsSectionRow>([
                    { key: "general", iconName: "user", label: "Général", onPress: onOpenGeneral },
                    { key: "address", iconName: "home", label: "Adresse", onPress: onOpenAddress },
                    { key: "profile", iconName: "graduation-cap", label: "Profil", onPress: onOpenProfile },
                    { key: "preferences", iconName: "sliders-h", label: "Réglages", onPress: onOpenPreferences },
                    onOpenTimezone && {
                        key: "timezone",
                        iconName: "globe",
                        label: "Fuseau horaire",
                        value: timezoneCity ?? undefined,
                        onPress: onOpenTimezone,
                    },
                ])}
            />
        </AccountSettingsTemplate>
    )
}
